import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useDeleteKudos } from "@/hooks/use-kudos";
import { useCurrentUser, isAdmin } from "@/hooks/use-current-user";
import type { Kudos } from "@/types/entities";

interface KudosCardProps {
  kudos: Kudos;
}

export function KudosCard({ kudos }: KudosCardProps) {
  const { currentUser } = useCurrentUser();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteKudos = useDeleteKudos();

  const canDelete =
    currentUser?.id === kudos.author_id || isAdmin(currentUser?.role);

  return (
    <>
      <Card>
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1">
              <p className="text-sm">{kudos.message}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {new Date(kudos.created_at).toLocaleDateString()}
              </p>
            </div>
            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Kudos</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this kudos? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteKudos.mutate(kudos.id, {
                  onSuccess: () => setDeleteDialogOpen(false),
                });
              }}
              disabled={deleteKudos.isPending}
            >
              {deleteKudos.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
